// Support-access impersonation — an aboo admin can, for a limited time, view a tenant's account
// exactly as that user sees it. The tenant reads out a one-time code (tenant/settings.js's
// "Support access" card generates it); an admin redeems it on admin/user_detail.js's Support
// access card (POST .../impersonate) and is dropped onto the tenant app as that user.
//
// The admin's OWN Keycloak session never changes (still the sleek-admin realm — see
// shared/auth.js). Impersonation is layered on top as an `X-Impersonate: <grant_token>` header —
// impersonationHeader(), attached to every api.js request alongside the admin's normal bearer
// token (see the build report's wiring notes) — so the API can tell WHO is impersonating and
// WHOM, for audit, without touching the admin's own auth at all.
//
// State lives in localStorage (survives reload; one grant at a time) under STORAGE_KEY as
// { token, expires_at, user, admin_email }. `impersonating` mirrors it in a signal SOLELY so
// impersonationBanner() can re-render in place on start/stop without a full route remount — its
// render reads ONLY that signal (never the mutating getter below), so nothing ever writes a
// signal from inside its own reactive computation. impersonationActive() is the actual source of
// truth: it always re-validates straight from localStorage and self-heals (clears + syncs the
// signal) the moment anything stale is found — a test seeding storage directly, another tab, a
// reload, all get picked up on the next call.
import { html, signal, navigate } from './t4.js';
import { api } from './api.js';
import { t } from './i18n.js';
import { currentUser } from './auth.js';

const STORAGE_KEY = 'sleek_impersonation';

function loadRaw(){
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function isValid(rec){
  return !!(rec && rec.token && rec.expires_at && new Date(rec.expires_at).getTime() > Date.now());
}
function clearStorage(){ try { localStorage.removeItem(STORAGE_KEY); } catch {} }

// Reactive mirror of the stored grant — exported mainly for test resets (mirrors bugReportOpen's
// beforeEach reset in bugreport.test.js); the shell owner MAY also read it to toggle a layout class,
// but impersonationActive() stays the one function that decides validity.
export const impersonating = signal(loadRaw());
function syncSignal(rec){
  const cur = impersonating.value;
  const same = (!cur && !rec) || (cur && rec && cur.token === rec.token && cur.expires_at === rec.expires_at);
  if(!same) impersonating.value = rec;
}

let autoStopTimer = null;
function clearAutoStop(){ if(autoStopTimer){ clearTimeout(autoStopTimer); autoStopTimer = null; } }
function scheduleAutoStop(expiresAt){
  clearAutoStop();
  const ms = expiresAt ? new Date(expiresAt).getTime() - Date.now() : -1;
  if(ms > 0) autoStopTimer = setTimeout(() => { stopImpersonation(); }, ms);
}

function formatHM(iso){
  const d = iso ? new Date(iso) : null;
  if(!d || isNaN(d.getTime())) return '';
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

// The single source of truth: is there a live grant RIGHT NOW. Always re-reads localStorage (never
// trusts only the cached signal), and self-heals — a stale/expired record is removed the moment
// anything asks. Returns the stored record ({token, expires_at, user, admin_email}) or null.
export function impersonationActive(){
  const rec = loadRaw();
  if(rec && !isValid(rec)){
    clearStorage();
    syncSignal(null);
    return null;
  }
  syncSignal(rec);
  return rec;
}

// Spread into every api.js request alongside the normal Authorization bearer (the admin's own
// token never changes — see shared/auth.js). null while inactive: { ...(impersonationHeader()||{}) }.
export function impersonationHeader(){
  const rec = impersonationActive();
  return rec ? { 'X-Impersonate': rec.token } : null;
}

// Called from admin/user_detail.js on a successful POST .../impersonate. `grant` is that response
// ({grant_token, expires_at, user}); `user` is passed explicitly (res.user) so the caller never has
// to guess which shape wins if the two ever disagree.
export function startImpersonation(grant, user){
  const rec = {
    token: grant && grant.grant_token,
    expires_at: grant && grant.expires_at,
    user: user || (grant && grant.user) || null,
    admin_email: currentUser.value || null,
  };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rec)); } catch {}
  impersonating.value = rec;
  scheduleAutoStop(rec.expires_at);
  navigate('/home');
}

// Exit — the banner's Exit button, or fired automatically when the grant's own timer runs out.
// Best-effort notify the API (an already-expired grant may itself refuse the call); the LOCAL
// session ends regardless — same tolerance as shared/auth.js's logout().
export async function stopImpersonation(){
  const rec = impersonationActive();
  try { if(rec && rec.token) await api.post('/admin/impersonation/stop', { grant_token: rec.token }); }
  catch { /* local exit still proceeds */ }
  clearImpersonation();
  navigate('/');
}

// When impersonation last ended — so shared/api.js can absorb the CONCURRENT stragglers: a tenant
// screen fires several requests at once, all carrying the (now-dead) grant header; the first ends
// impersonation, and the rest arrive 401 a beat later, after a refresh() round-trip. Any 401
// within this window of an exit must return the admin to their console, never log them out.
let endedAt = 0;

// Local-only exit: forget the grant WITHOUT notifying the API. Used by shared/api.js when a
// request 401s under a dead grant (drop impersonation, return the admin to their console instead
// of logging them out), and by shared/auth.js on sign-in / sign-out so a fresh admin session is
// never contaminated by a leftover record. stopImpersonation() is the deliberate, API-notifying exit.
export function clearImpersonation(){
  clearAutoStop();
  clearStorage();
  impersonating.value = null;
  endedAt = Date.now();
}

// True while impersonating OR within `ms` of the last exit — the gate shared/api.js uses to decide
// a 401 means "the support-access grant died", not "the admin's own session is invalid".
export function recentlyImpersonating(ms = 8000){
  return !!impersonationActive() || (Date.now() - endedAt < ms);
}

// Resume the auto-stop timer across a page reload while a grant is still live.
scheduleAutoStop((impersonationActive() || {}).expires_at);

// LAZY (nothing in the DOM while inactive) — copies shared/version.js's releaseNotesModal idiom.
// The shell owner mounts this once in BOTH tshell.js and admin/shell.js (see the build report).
// Reads ONLY the reactive signal (never the mutating impersonationActive()) so this render can never
// write a signal from inside its own computation; isValid() here is a pure, side-effect-free
// re-check that covers the brief window between the grant's expiry and the scheduled auto-stop
// timer actually firing (setTimeout can lag a background tab).
export function impersonationBanner(){
  return html`${() => {
    const rec = impersonating.value;
    if(!rec || !isValid(rec)) return '';
    const u = rec.user || {};
    const name = u.display_name || u.email || t('impersonate.someone', 'this user');
    return html`<div class="impersonate-banner" role="status">
      <span class="imp-banner-text">👁 ${t('impersonate.viewing_as', 'Viewing as')}
        <b>${name}</b>${u.email ? html` (${u.email})` : ''}
        — ${t('impersonate.ends', 'ends')} ${formatHM(rec.expires_at)}</span>
      <button type="button" class="btn btn-sm imp-exit-btn" @click=${() => stopImpersonation()}>${t('impersonate.exit', 'Exit')}</button>
    </div>`;
  }}`;
}
